import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { Button } from "primereact/button";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { useCreateEligibilityMutation } from "store/queries/eligibility";
import {
  IEligibilityMember,
  IEligibilityProvider,
  IProvider,
  IRecommendedPayerBasedOnEligibility,
} from "types/Provider/Provider";

import {
  buildOutOfNetworkOnlyBenefitsMessage,
  buildPayerSwitchMessage,
} from "../eligibilityMessages";
import { buildEligibilityProcedures } from "../eligibilityProcedures";

export function EligibilityButton({
  accountId,
  mainProvider,
  errorMessageSetter,
  disabled,
  isSecondaryInsurance = false,
}: {
  accountId: string;
  mainProvider: IProvider;
  errorMessageSetter: React.Dispatch<React.SetStateAction<string | null>>;
  disabled: boolean;
  isSecondaryInsurance?: boolean;
}) {
  const { getValues, setValue } = useFormContext();
  const [runningEligibility, setRunningEligibility] = useState(false);
  const { payersCodesInfo, payersNameInfo } = useSelector(
    (state: any) => state.orderSlice
  );
  const [createEligibility] = useCreateEligibilityMutation();

  const dateOfServiceStart = dayjs().format("MM/DD/YYYY");
  const dateOfServiceEnd = dayjs().format("MM/DD/YYYY");

  const mapValuesToEligibility = async () => {
    errorMessageSetter("");
    const { name: providerName, npi } = mainProvider;
    const {
      firstName: memberFirstName,
      lastName: memberLastName,
      dateOfBirth: memberDOB,
      memberId,
      memberId2,
      payerName,
      payerName2,
      isSubscriberPatient,
      practiceType,
      practiceType2,
      serviceDetails,
    } = getValues();

    // CLE-2820 — the procedures already chosen, if any, for the AI second opinion's context only.
    const procedures = buildEligibilityProcedures(serviceDetails);

    const provider: IEligibilityProvider = {
      firstName: providerName,
      middleName: providerName,
      lastName: providerName,
      npi,
    };

    const member: IEligibilityMember = {
      firstName: memberFirstName,
      lastName: memberLastName,
      dob: memberDOB,
      memberID: isSecondaryInsurance ? memberId2 : memberId,
    };

    const payerCodeForPrimaryInsurance =
      payersCodesInfo.find(
        (payer: { name: string; code: string[] }) => payer.name === payerName
      )?.code[0] || "";
    const payerCodeForSecondaryInsurance =
      payersCodesInfo.find(
        (payer: { name: string; code: string[] }) => payer.name === payerName2
      )?.code[0] || "";

    const payerCode = isSecondaryInsurance
      ? payerCodeForSecondaryInsurance
      : payerCodeForPrimaryInsurance;

    const eligibility = {
      accountId,
      payerCode: payerCode || "",
      payerName: isSecondaryInsurance ? payerName2 : payerName,
      provider,
      member,
      isSubscriberPatient: Boolean(isSubscriberPatient),
      // CLE-2729 — the server cannot work this out for itself. Nothing in a pVerify response says
      // which side of the order it serves, and the order link that would say so is written after
      // the check returns. It is what keeps a secondary plan's $0 — which is $0 only because the
      // primary already paid — out of the primary side of the historical benefit index.
      isSecondaryInsurance,
      practiceTypeCode: isSecondaryInsurance ? practiceType2 : practiceType,
      dateOfServiceStart,
      dateOfServiceEnd,
      ...(procedures.length > 0 && { procedures }),
    };
    setRunningEligibility(true);
    const eligibilityCall = await createEligibility({ body: eligibility });
    setRunningEligibility(false);
    const data = (eligibilityCall as any)?.data?.data || null;

    return { request: eligibility, response: data };
  };

  const runEligibility = async () => {
    const eligibilityData = await mapValuesToEligibility();
    const eligibilityResponse = eligibilityData.response;

    if (isEmpty(eligibilityResponse)) {
      errorMessageSetter(`No eligibility response found. 
      Please contact the payer via phone or payer's portal to obtain the patient's current benefit information`);
      return;
    }

    if (Object.prototype.hasOwnProperty.call(eligibilityResponse, "error")) {
      errorMessageSetter(
        `Eligibility Verification Error: ${eligibilityResponse.error}`
      );
      return;
    }

    if (eligibilityResponse.planStatus === "Inactive") {
      errorMessageSetter(
        "The patient coverage shows inactive, please contact the patient or the payer to obtain most current information"
      );
      return;
    }

    const isServiceDetailEmpty = (serviceDetail: any) => {
      return (
        serviceDetail == null ||
        (typeof serviceDetail === "object" &&
          Object.keys(serviceDetail).length === 0)
      );
    };
    const hospitalOutpatientIsEmpty = isServiceDetailEmpty(
      eligibilityResponse.details?.HospitalOutPatientSummary
    );
    const mriCtScanIsEmpty = isServiceDetailEmpty(
      eligibilityResponse.details?.MRI_CT_ScanSummary
    );
    const allFieldsAreNull =
      (hospitalOutpatientIsEmpty && mriCtScanIsEmpty) ||
      (eligibilityResponse.familyCalendarYearDeductible === null &&
        eligibilityResponse.individualCalendarYearDeductible === null &&
        eligibilityResponse.remainingDeductible === null &&
        eligibilityResponse.coinsurancePercent === null);

    // Handle recommended payer based on eligibility
    if (eligibilityResponse.recommendedPayerBasedOnEligibility) {
      const recommendedPayer: IRecommendedPayerBasedOnEligibility =
        eligibilityResponse.recommendedPayerBasedOnEligibility;

      // Find the recommended payer in the available payers list
      const recommendedPayerOption = payersNameInfo.find(
        (payer: any) => payer.name === recommendedPayer.name
      );

      if (recommendedPayerOption) {
        // Show a message to the user about the automatic payer update
        errorMessageSetter(
          buildPayerSwitchMessage(
            recommendedPayer,
            eligibilityData.request.payerName,
            eligibilityResponse.planCoverageSummary?.PlanName
          )
        );
        // CLE-2845 — the stored request keeps the payer the check was SENT under. The API's
        // `wasPayerChangedSinceEligibility` recognises this switch from the recommendation on the
        // stored response, so the monthly re-run does not read it as a hand edit.
        setValue(
          isSecondaryInsurance ? "payerName2" : "payerName",
          recommendedPayer.name
        );
        setValue(isSecondaryInsurance ? "payer2" : "payer", {
          name: recommendedPayer.name,
          payerId: recommendedPayer.id,
        });
      }
    }

    // Always set benefitDetails BEFORE any early returns so we can display
    // basic plan coverage info even when detailed benefits aren't available
    setValue(
      isSecondaryInsurance ? "benefitDetails2" : "benefitDetails",
      eligibilityData
    );
    // Store the pverifyResponseId if available
    if (eligibilityResponse.pverifyResponseId) {
      setValue(
        isSecondaryInsurance
          ? "secondaryPverifyResponseId"
          : "pverifyResponseId",
        eligibilityResponse.pverifyResponseId
      );
    }
    if (isSecondaryInsurance) {
      setValue("secondaryBenefitsUpdatedAt", new Date().toISOString());
    } else {
      setValue("benefitsUpdatedAt", new Date().toISOString());
    }

    // CLE-2882 — the account is out of network for this plan, and nothing in-network is read. A field
    // with no out-of-network figure behind it — null, or a 0 the API flagged empty — is cleared rather
    // than left holding a $0 or an earlier check's number, so staff confirm it with the payer. Done
    // before the early returns below so it holds on every path.
    const isOutOfNetwork =
      eligibilityResponse.networkStatusOverride?.status === "OutOfNetwork";
    const blankOutOfNetwork = {
      deductible:
        isOutOfNetwork &&
        (eligibilityResponse.remainingDeductible === null ||
          !!eligibilityResponse.isRemainingDeductibleEmpty),
      outOfPocketMax:
        isOutOfNetwork &&
        (eligibilityResponse.remainingOutOfPocketMax === null ||
          !!eligibilityResponse.isRemainingOutOfPocketMaxEmpty),
      flatCopay: isOutOfNetwork && eligibilityResponse.flatCopay === null,
      coinsurancePercent:
        isOutOfNetwork &&
        (eligibilityResponse.coinsurancePercent === null ||
          !!eligibilityResponse.isCoinsurancePercentEmpty),
    };
    Object.entries(blankOutOfNetwork).forEach(([field, blank]) => {
      if (blank) setValue(isSecondaryInsurance ? `${field}2` : field, null);
    });

    // CLE-2675 — the payer answered, but every benefit it disclosed is out-of-network. The
    // normalized values are zeros of ignorance, not a real $0 plan: auto-filling them prices
    // the estimate off benefits the payer never disclosed. The response stays stored above
    // (same as the empty-benefits case); only the form fields are withheld. Manual entry
    // after calling the payer remains open.
    if (eligibilityResponse.hasOnlyOutOfNetworkBenefits) {
      errorMessageSetter(
        buildOutOfNetworkOnlyBenefitsMessage(eligibilityResponse.additionalInfo)
      );
      return;
    }

    if (eligibilityResponse.isEligibilityEmpty || allFieldsAreNull) {
      errorMessageSetter(eligibilityResponse.additionalInfo);
      return;
    }

    if (
      eligibilityResponse.coinsurancePercent !== null &&
      !blankOutOfNetwork.coinsurancePercent
    )
      setValue(
        isSecondaryInsurance ? "coinsurancePercent2" : "coinsurancePercent",
        Number(eligibilityResponse.coinsurancePercent)
      );
    if (eligibilityResponse.flatCopay !== null && !blankOutOfNetwork.flatCopay)
      setValue(
        isSecondaryInsurance ? "flatCopay2" : "flatCopay",
        Number(eligibilityResponse.flatCopay)
      );

    // Handle professional copay automation
    if (
      eligibilityResponse.professionalCopay !== null &&
      eligibilityResponse.professionalCopay > 0
    ) {
      setValue(
        isSecondaryInsurance
          ? "professionalFlatCopay2"
          : "professionalFlatCopay",
        Number(eligibilityResponse.professionalCopay)
      );
      // Auto-trigger professional copay display
      const profCopayTrigger = new CustomEvent("triggerProfessionalCopay", {
        detail: { value: eligibilityResponse.professionalCopay },
      });
      window.dispatchEvent(profCopayTrigger);
    }
    if (
      eligibilityResponse.remainingDeductible !== null &&
      !blankOutOfNetwork.deductible
    )
      setValue(
        isSecondaryInsurance ? "deductible2" : "deductible",
        Number(eligibilityResponse.remainingDeductible)
      );
    if (
      eligibilityResponse.remainingOutOfPocketMax !== null &&
      !blankOutOfNetwork.outOfPocketMax
    )
      setValue(
        isSecondaryInsurance ? "outOfPocketMax2" : "outOfPocketMax",
        Number(eligibilityResponse.remainingOutOfPocketMax)
      );
    if (eligibilityResponse.isRemainingOutOfPocketMaxInfinity !== null)
      setValue(
        isSecondaryInsurance
          ? "isRemainingOutOfPocketMaxInfinity2"
          : "isRemainingOutOfPocketMaxInfinity",
        eligibilityResponse.isRemainingOutOfPocketMaxInfinity
      );
  };

  return (
    <div
      data-testid="footer_PayerCardFooter"
      className="flex justify-content-end"
    >
      <Button
        label="Run Eligibility"
        onClick={runEligibility}
        loading={runningEligibility}
        disabled={disabled}
        className="h-fit"
        icon="pi pi-sync"
      />
    </div>
  );
}
